import React, { useEffect, useState, VoidFunctionComponent } from "react";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";
import { LayerLayout } from "@/components/responsive/product/layer/layerLayout/LayerLayout";
import { Description } from "@/components/responsive/product/layer/description/Description";
import { EcoConception } from "@/components/responsive/product/layer/ecoConception/EcoConception";
import { Features } from "@/components/responsive/product/layer/features/Features";
import { RatingLayer } from "@/components/responsive/product/layer/rating/RatingLayer";
import { QuestionLayer } from "@/components/responsive/product/layer/question/Question";
import { AskQuestion } from "@/components/responsive/product/layer/question/AskQuestion";
import { Documentation } from "@/components/responsive/product/layer/documentation/Documentation";
import { CreditLayer } from "@/components/responsive/product/layer/credit/CreditLayer";
import { SellerServiceLayer } from "@/components/responsive/product/layer/sellerServices/SellerService";
import { OldProductLayer } from "@/components/responsive/product/layer/oldProduct/OldProduct";
import { ChoiceBox } from "@/components/responsive/product/layer/choiceBox/ChoiceBox";
import { BundleLayer } from "@/components/responsive/product/layer/bundle/Bundle";
import { WarrantiesLayer } from "@/components/responsive/product/layer/warrantiesLayer/WarrantiesLayer";
import { ExtendedWarranty } from "@/models/scope/productSheet/ExtendedWarranty";
import { ProductReviewListStatistics } from "@/models/scope/productSheet/reviews/ProductReviewListStatistics";
import { ProductDocumentList } from "@/models/scope/productSheet/ProductDocumentList";
import { ProductReviewQuestions } from "@/models/scope/productSheet/ProductReviewQuestions";
import { Review } from "@/models/scope/productSheet/reviews/Review";
import { MkpSeller } from "@/models/scope/productSheet/mkpSeller/MkpSeller";
import { ProductFeature } from "@/models/scope/productSheet/ProductFeature";
import { RootState } from '@/models/appState/RootState';
import { Offers } from "@/models/scope/productSheet/offer/Offers";
import { ShippingRelatedContent } from "@/models/scope/shipping/ShippingRelatedContent";
import { LAYER_TYPE } from "@/utils/constants";
import {ProductFinancingOffer} from "@/models/scope/productSheet/ProductFinancingOffer";

const StockLocator = dynamic(
    () => import('@/components/responsive/product/layer/stockLocator/StockLocator'),
    { ssr: false }
);
const ShippingLayer = dynamic(
    () => import('@/components/responsive/product/layer/shippingLayer/ShippingLayer'),
    { ssr: false }
);
const RattachmentLayer = dynamic(
    () => import('@/components/responsive/product/layer/rattachmentLayer/RattachmentLayer'),
    { ssr: false }
);
const ShippingInfo = dynamic(
    () => import('@/components/responsive/product/layer/shippingInfo/ShippingInfo'),
    { ssr: false }
);
const OptionsInfo = dynamic(
    () => import('@/components/responsive/product/layer/OptionsInfo/OptionsInfo'),
    { ssr: false }
);
const Basket = dynamic(
    () => import('@/components/responsive/product/layer/basket/Basket'),
    { ssr: false }
);
const BasketHeader = dynamic(
    () => import('@/components/responsive/product/layer/basket/basketHeader/BasketHeader'),
    { ssr: false }
);

interface Props {
    selectedProductId: string;
    isShow: boolean;
    onClose: () => void;
    setLayerType: (layerType: string) => void;
    sellerServiceData?: MkpSeller;
    contentToDisplay: string;
    description: string;
    features: ProductFeature[];
    ecoScore: string | undefined;
    reviewListStatistics: ProductReviewListStatistics;
    reviews: Review[];
    reviewQuestions: ProductReviewQuestions;
    documentList: ProductDocumentList<string> | null;
    videoUrl: string;
    urlVideoAssembly: string;
    urlNotice: string;
    newEnergyClass: string | null;
    repairabilityIndex: string | null;
    frontLabel: string;
    title: string;
    endDateRI: string;
    markLabel: string;
    type: string;
    warranties: ExtendedWarranty[];
    setWarrantySelected: (status: boolean) => void;
    isSelectedWarranty: boolean;
    seller: MkpSeller | undefined;
    offerWarranty: number | undefined;
    offerWarrantyUnit: string | undefined;
    state: string | undefined;
    offerList: Offers | null;
    soldBy: string;
    shippingRelatedContent: ShippingRelatedContent;
    setShowCloseLayerButton: (status: boolean) => void;
    showCloseLayerButton: boolean;
    mainProductId: string;
    saleability: string;
    isMobile: boolean;
    setPriceIncludeVAT: (includeVAT: boolean) => void;
    priceIncludeVAT: boolean;
    financingOffers: ProductFinancingOffer[],
}

export const Layer: VoidFunctionComponent<Props> = ({
    isShow,
    onClose,
    contentToDisplay,
    endDateRI,
    description,
    features,
    ecoScore,
    reviewListStatistics,
    reviews,
    setLayerType,
    reviewQuestions,
    documentList,
    videoUrl,
    urlVideoAssembly,
    urlNotice,
    repairabilityIndex,
    newEnergyClass,
    frontLabel,
    title,
    markLabel,
    type,
    warranties,
    setWarrantySelected,
    selectedProductId,
    offerWarrantyUnit,
    offerWarranty,
    state,
    seller,
    offerList,
    soldBy,
    shippingRelatedContent,
    setShowCloseLayerButton,
    showCloseLayerButton,
    sellerServiceData,
    isSelectedWarranty,
    mainProductId,
    saleability,
    isMobile,
    setPriceIncludeVAT,
    priceIncludeVAT,
    financingOffers
}) => {
    const [classToApply, setClassToApply] = useState('none');
    const [questionIsSend, setQuestionIsSend] = useState(false);
    const [shippingInfo, setShippingInfo] = useState({
        title: '',
        contents: [''],
        backTo: LAYER_TYPE.NONE,
    });
    const [optionsInfo, setOptionsInfo] = useState({
        title: '',
        contents: '',
        backTo: LAYER_TYPE.NONE,
    });
    const cartErrorStatus: boolean = useSelector((state: RootState) => state.header.cartUpdateError);

    useEffect(() => {
        if (isShow) {
            document.body.classList.add('no-scroll');
            setClassToApply('fade-in');
            setTimeout(() => setClassToApply(''), 500);
        } else {
            document.body.classList.remove('no-scroll');
        }
    }, [isShow]);

    const handleClose = (): void => {
        setClassToApply('fade-out');
        setTimeout(() => {
            onClose();
            setClassToApply('none');
        }, 500);
    };

    return (
        <div className={`layer ${classToApply}`} onClick={handleClose}>
            {offerList && offerList.items.length > 1 ? (
                <LayerLayout
                    title="Services vendeur partenaire"
                    isDisplayed={contentToDisplay === LAYER_TYPE.SELLER_SERVICE}
                    onClose={() => handleClose()}
                    classToApply={classToApply}
                    onBackHandler={showCloseLayerButton ? (() => setLayerType(LAYER_TYPE.CHOICE_BOX)) : undefined}
                >
                    <SellerServiceLayer
                        seller={sellerServiceData}
                        offerList={offerList}
                        offerWarranty={offerWarranty}
                        offerWarrantyUnit={offerWarrantyUnit}
                        state={state}
                        handleClick={setLayerType}
                    />
                </LayerLayout>
            ) : (
                <LayerLayout
                    title="Services vendeur partenaire"
                    isDisplayed={contentToDisplay === LAYER_TYPE.SELLER_SERVICE}
                    onClose={handleClose}
                    classToApply={classToApply}
                >
                    <SellerServiceLayer
                        seller={seller}
                        offerList={offerList}
                        offerWarranty={offerWarranty}
                        offerWarrantyUnit={offerWarrantyUnit}
                        state={state}
                        handleClick={setLayerType}
                    />
                </LayerLayout>
            )}
            <LayerLayout
                title='Reprise de votre ancien produit'
                isDisplayed={contentToDisplay === LAYER_TYPE.OLD_PRODUCT}
                onClose={handleClose}
                classToApply={classToApply}
                onBackHandler={() => setLayerType(LAYER_TYPE.SELLER_SERVICE)}
            >
                <OldProductLayer sellerUrl={seller?.sheetUrl}/>
            </LayerLayout>
            {offerList && <LayerLayout
                title={`Toutes les offres partenaires (${offerList?.items?.length - 1})`}
                isDisplayed={contentToDisplay === LAYER_TYPE.CHOICE_BOX}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <ChoiceBox
                    offerList={offerList}
                    setLayerType={setLayerType}
                    endDateRI={endDateRI}
                    priceIncludeVAT={priceIncludeVAT}
                    setPriceIncludeVAT={setPriceIncludeVAT}
                    isPei soldBy={soldBy} productId={selectedProductId}
                    setShowCloseLayerButton={setShowCloseLayerButton} />
            </LayerLayout>}
            <LayerLayout
                title={'Descriptif produit'}
                isDisplayed={contentToDisplay === LAYER_TYPE.DESCRIPTION}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <Description description={description} />
            </LayerLayout>
            <LayerLayout
                customHeader={cartErrorStatus ? undefined : <BasketHeader />}
                isDisplayed={contentToDisplay === LAYER_TYPE.BASKET}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <Basket
                    frontLabel={frontLabel}
                    title={title}
                    markLabel={markLabel}
                    type={type}
                    warranties={warranties}
                    productId={selectedProductId}
                    contentToDisplay={contentToDisplay}
                    errorStatus={cartErrorStatus}
                    isSelectedWarranty={isSelectedWarranty}
                    setWarrantySelected={setWarrantySelected} />
            </LayerLayout>
            <LayerLayout
                title={'Caractéristiques'}
                isDisplayed={contentToDisplay === LAYER_TYPE.FEATURES}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <Features features={features} />
            </LayerLayout>
            <LayerLayout
                title={'Avis client'}
                isDisplayed={contentToDisplay === LAYER_TYPE.RATING}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <RatingLayer
                    productId={selectedProductId}
                    reviewListStatistics={reviewListStatistics}
                    reviews={reviews}
                />
            </LayerLayout>
            <LayerLayout
                title={'Questions et réponses'}
                isDisplayed={contentToDisplay === LAYER_TYPE.QUESTION}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <QuestionLayer
                    setLayerType={setLayerType}
                    reviewQuestions={reviewQuestions}
                    productId={selectedProductId}
                />
            </LayerLayout>
            <LayerLayout
                title={'Posez votre question'}
                isDisplayed={contentToDisplay === LAYER_TYPE.ASK_QUESTION}
                onClose={handleClose}
                classToApply={classToApply}
                onBackHandler={() => {
                    setLayerType(LAYER_TYPE.QUESTION);
                    setQuestionIsSend(false);
                }}
            >
                <AskQuestion
                    setQuestionIsSend={setQuestionIsSend}
                    questionIsSend={questionIsSend}
                    productId={selectedProductId}
                />
            </LayerLayout>
            <LayerLayout
                title={'Eco-conception'}
                isDisplayed={contentToDisplay === LAYER_TYPE.ECO_CONCEPTION}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <EcoConception ecoScore={ecoScore} />
            </LayerLayout>
            <LayerLayout
                title={'Documentation produit'}
                isDisplayed={contentToDisplay === LAYER_TYPE.DOCUMENTATION}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <Documentation documentList={documentList} urlNotice={urlNotice} urlVideoAssembly={urlVideoAssembly} newEnergyClass={newEnergyClass} repairabilityIndex={repairabilityIndex}/>
            </LayerLayout>
            {financingOffers.length > 0 && financingOffers[0].butEligibility == 1  &&
            <LayerLayout
                title={'Je paye à mon rythme sur but.fr'}
                isDisplayed={contentToDisplay === LAYER_TYPE.CREDIT}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <CreditLayer />
            </LayerLayout>
            }
            <LayerLayout
                title={'Disponible près de chez moi'}
                isDisplayed={contentToDisplay === LAYER_TYPE.STOCK_LOCATOR}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <StockLocator
                    productId={selectedProductId}
                    mainProductId={mainProductId}
                    isDisplayed={contentToDisplay === LAYER_TYPE.STOCK_LOCATOR}
                    shippingRelatedContent={shippingRelatedContent}
                    setShippingInfo={setShippingInfo}
                    setOptionsInfo={setOptionsInfo}
                    onClose={handleClose}
                    setLayerType={setLayerType}
                    saleability={saleability}
                    isMobile={isMobile}
                />
            </LayerLayout>
            <LayerLayout
                title={'Livraisons disponibles'}
                isDisplayed={contentToDisplay === LAYER_TYPE.SHIPPING}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <ShippingLayer
                    setShippingInfo={setShippingInfo}
                    setOptionsInfo={setOptionsInfo}
                    shippingRelatedContent={shippingRelatedContent}
                    setLayerType={setLayerType}
                    productId={mainProductId}
                    isDisplayed={contentToDisplay === LAYER_TYPE.SHIPPING}
                />
            </LayerLayout>
            <LayerLayout
                title={'Vérifier la disponibilité'}
                isDisplayed={contentToDisplay === LAYER_TYPE.ATTACHEMENT}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <RattachmentLayer
                    productId={mainProductId}
                    onClose={handleClose}
                />
            </LayerLayout>
            <LayerLayout
                isDisplayed={contentToDisplay === LAYER_TYPE.SHIPPING_INFO}
                onClose={handleClose}
                classToApply={classToApply}
                onBackHandler={() => setLayerType(shippingInfo.backTo)}
            >
                <ShippingInfo shippingInfo={shippingInfo} />
            </LayerLayout>
            <LayerLayout
                isDisplayed={contentToDisplay === LAYER_TYPE.OPTIONS_INFO}
                onClose={handleClose}
                classToApply={classToApply}
                onBackHandler={() => setLayerType(optionsInfo.backTo)}
            >
                <OptionsInfo optionsInfo={optionsInfo} />
            </LayerLayout>
            <LayerLayout
                title={warranties?.[0]?.popinTitle}
                isDisplayed={contentToDisplay === LAYER_TYPE.WARRANTIES}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <WarrantiesLayer extendedWarranties={warranties} setWarrantySelected={setWarrantySelected} onClose={handleClose}/>
            </LayerLayout>
            <LayerLayout
                isDisplayed={contentToDisplay === LAYER_TYPE.BUNDLE}
                onClose={handleClose}
                classToApply={classToApply}
            >
                <BundleLayer isMobile={isMobile} />
            </LayerLayout>
        </div>
    );
}